import { useMemo } from "react";

import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { Alert, Box, CircularProgress, Stack, ToggleButton, Typography } from "@mui/material";
import { format } from "date-fns";

import { AppointmentResponse } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

interface Props {
  /** Until a Zweck is chosen only a hint is shown instead of slots. */
  purposeSelected: boolean;
  /** The Berater:in is clearable and filters the slots, so nothing is requested without one. */
  counselorSelected: boolean;
  showCounselor: boolean;
  slots: AppointmentResponse[];
  isLoading: boolean;
  isError: boolean;
  selectedSlotId: string | null;
  onSelect: (appointmentId: string) => void;
}

interface DayGroup {
  dayLabel: string;
  slots: AppointmentResponse[];
}

export function FreeSlotList({
  purposeSelected,
  counselorSelected,
  showCounselor,
  slots,
  isLoading,
  isError,
  selectedSlotId,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  // The backend returns the slots chronologically; grouping by day is display-only.
  const dayGroups = useMemo(() => {
    const groups: DayGroup[] = [];
    for (const slot of slots) {
      const dayLabel = format(slot.startTimeUtc, "EE dd.MM.yyyy", { locale: locales[language] });
      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.dayLabel === dayLabel) {
        lastGroup.slots.push(slot);
      } else {
        groups.push({ dayLabel, slots: [slot] });
      }
    }
    return groups;
  }, [slots, language]);

  // Both "choose X first" states are the same hint with a different sentence, so only the sentence
  // is decided here and the hint itself is rendered once below.
  const selectionHint = !purposeSelected
    ? t("clients.client.appointmentCreation.dialog.selectPurposeHint")
    : !counselorSelected
      ? t("clients.client.appointmentCreation.dialog.selectCounselorHint")
      : null;

  return (
    <Box
      sx={{
        backgroundColor: "action.hover",
        borderRadius: 1,
        p: 2,
        height: 280,
        overflowY: "auto",
      }}
    >
      {selectionHint !== null ? (
        <CenteredHint>
          <EventAvailableIcon color="disabled" fontSize="large" />
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            {selectionHint}
          </Typography>
        </CenteredHint>
      ) : isLoading ? (
        <CenteredHint>
          <CircularProgress aria-label={t("clients.client.appointmentCreation.dialog.loadingSlots")} />
        </CenteredHint>
      ) : isError ? (
        <CenteredHint>
          <Alert severity="error" sx={{ width: "100%" }}>
            {t("clients.client.appointmentCreation.dialog.slotsLoadError")}
          </Alert>
        </CenteredHint>
      ) : dayGroups.length === 0 ? (
        <CenteredHint>
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
            {t("clients.client.appointmentCreation.dialog.noFreeSlots")}
          </Typography>
        </CenteredHint>
      ) : (
        <Stack spacing={2} role="group" aria-label={t("clients.client.appointmentCreation.dialog.slotListAriaLabel")}>
          {dayGroups.map((group) => (
            <Box key={group.dayLabel}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {group.dayLabel}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                {group.slots.map((slot) => (
                  <SlotCard
                    key={slot.appointmentId}
                    slot={slot}
                    selected={selectedSlotId === slot.appointmentId}
                    onSelect={() => onSelect(slot.appointmentId)}
                    locationLabel={slot.location ?? ""}
                    counselorUserCode={showCounselor ? (slot.assignedCounselorUserCode ?? null) : null}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function CenteredHint({ children }: { children: React.ReactNode }) {
  return (
    <Stack spacing={1} sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
      {children}
    </Stack>
  );
}

interface SlotCardProps {
  slot: AppointmentResponse;
  selected: boolean;
  onSelect: () => void;
  locationLabel: string;
  counselorUserCode: string | null;
}

function SlotCard({ slot, selected, onSelect, locationLabel, counselorUserCode }: SlotCardProps) {
  const timeRange = `${format(slot.startTimeUtc, "HH:mm")}–${format(slot.endTimeUtc, "HH:mm")}`;
  // The secondary text colour is unreadable on the primary fill of a selected card.
  const captionColor = selected ? "inherit" : "text.secondary";

  return (
    <ToggleButton
      value={slot.appointmentId}
      selected={selected}
      onChange={onSelect}
      sx={{
        backgroundColor: "background.paper",
        textTransform: "none",
        flexDirection: "column",
        alignItems: "flex-start",
        px: 2,
        py: 1,
        minWidth: 140,
        "&.Mui-selected": {
          backgroundColor: "primary.light",
          borderColor: "primary.main",
          color: "primary.contrastText",
          "&:hover": {
            backgroundColor: "primary.light",
            color: "primary.contrastText",
          },
          "&.Mui-focusVisible": {
            backgroundColor: "primary.light",
            color: "primary.contrastText",
          },
        },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {timeRange}
      </Typography>
      {locationLabel && (
        <Typography variant="caption" color={captionColor}>
          {locationLabel}
        </Typography>
      )}
      {counselorUserCode && (
        <Typography variant="caption" color={captionColor}>
          {counselorUserCode}
        </Typography>
      )}
    </ToggleButton>
  );
}
